/**
 * A bare HTTP client for calls that must NOT go through the shared API
 * client's interceptors:
 *
 *  - device registration sends the sign-in ("parent") token, not the store's;
 *  - field ingest sends the short-lived tracker credential, and an expired
 *    tracker credential's 401 must never trigger the shared client's
 *    "401 = signed out" handler.
 *
 * Every response is returned (no throw on 4xx/5xx) so callers classify it
 * themselves; only a request that got no response at all (offline, timeout,
 * DNS) rejects.
 */
import axios, { type AxiosInstance } from 'axios';
import { API_URL } from './api';

export interface RawResponse {
  status: number;
  data: unknown;
}

export type RawPost = (
  path: string,
  body: unknown,
  headers: Record<string, string>,
  options?: { timeoutMs?: number }
) => Promise<RawResponse>;

let client: AxiosInstance | null = null;

/** Created on first use, so importing this module has no side effects. */
function getClient(): AxiosInstance {
  if (!client) {
    client = axios.create({
      baseURL: `${API_URL}/api`,
      headers: { 'Content-Type': 'application/json' },
    });
  }
  return client;
}

export const rawPost: RawPost = async (path, body, headers, options) => {
  const res = await getClient().post(path, body, {
    headers,
    timeout: options?.timeoutMs ?? 20_000,
    validateStatus: () => true,
  });
  return { status: res.status, data: res.data };
};

/** The error code of an `{ error: { code } }` body, or null. */
export function errorCodeOf(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const code = (data as { error?: { code?: unknown } }).error?.code;
  return typeof code === 'string' ? code : null;
}
